import React from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Typography } from '@mui/material';
import Popover from '@mui/material/Popover';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { type FilterItem } from './types';
import { Form, FormSpy } from 'react-final-form';
import isEmpty from 'lodash/isEmpty';
import { omitBy } from 'lodash';
import { type FormApi, type FormState } from 'final-form';
import { FilterGropItem } from './components/FilterGropItem';
import { FilterGropItemWrapper } from './styles';
import { FilterClearButton } from './components/FilterClearButton';

type FiltersWrapperProps = {
  isOpen: boolean;
};

const FiltersWrapper = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isOpen'
})<FiltersWrapperProps>(({ theme, isOpen }) => {
  return {
    color: isOpen ? theme.palette.primary.main : theme.palette.text.primary,
    cursor: 'pointer',
    borderRadius: '20px',
    transition: 'background-color 150ms ease-in',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  };
});

type FiltersProps<T> = {
  filterScheme: FilterItem[];
  setFilterValue: (values: T | null) => void;
  resetFilters?: () => void;
  initialValues?: T;
};

export const Filters = <T,>({ filterScheme, setFilterValue, resetFilters, initialValues }: FiltersProps<T>) => {
  const { t } = useTranslation(['filters']);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleValuesChange = ({ values }: FormState<T>) => {
    queueMicrotask(() => {
      const filteredValues = omitBy(values as object, isEmpty) as T;
      if (isEmpty(filteredValues)) {
        setFilterValue(null);
      } else {
        setFilterValue(filteredValues);
      }
    });
  };

  const isOpen = Boolean(anchorEl);

  const submitCallback = (_: FormState<T>, form: FormApi<T>) => {
    form.reset();
  };

  return (
    <Form initialValues={initialValues} onSubmit={submitCallback as unknown as any}>
      {({ handleSubmit }) => {
        return (
          <>
            <FormSpy<T> subscription={{ values: true }} onChange={handleValuesChange} />
            <FiltersWrapper isOpen={isOpen} variant="subtitle1" onClick={handleClick}>
              <FilterListIcon />
              <span>{t('apply filters')}</span>
            </FiltersWrapper>
            <Popover
              open={isOpen}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 50,
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
            >
              <Box sx={{ p: 2, minWidth: '200px' }}>
                {filterScheme.map(el => (
                  <FilterGropItemWrapper key={el.name}>
                    <FilterGropItem filterItem={el} />
                  </FilterGropItemWrapper>
                ))}
              </Box>
              <FilterClearButton resetFilters={resetFilters} handleSubmit={handleSubmit} handleClose={handleClose} />
            </Popover>
          </>
        );
      }}
    </Form>
  );
};
